import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  arrayUnion,
} from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import { CoOrganizer, CoOrganizerPermission } from 'src/@types/event';
import { EventsApi } from './events.api';

// ==================== CO-ORGANIZERS API ====================

export const CoOrganizersApi = createApi({
  reducerPath: 'coOrganizersApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['CoOrganizer'],
  endpoints: (builder) => ({
    /**
     * Add a co-organizer to an event
     */
    addCoOrganizer: builder.mutation<
      { coOrganizer: CoOrganizer },
      {
        eventId: string;
        eventTitle: string;
        mainOrgName: string;
        orgId: string;
        orgName: string;
        orgLogo?: string;
        permissions: CoOrganizerPermission;
        addedBy: string;
      }
    >({
      async queryFn(data) {
        try {
          const eventRef = doc(DB, 'events', data.eventId);
          const eventSnap = await getDoc(eventRef);

          if (!eventSnap.exists()) {
            return { error: { status: 'NOT_FOUND', error: 'Event not found' } };
          }

          const eventData = eventSnap.data();
          const currentCoOrgs: CoOrganizer[] = eventData?.coOrganizers || [];

          // Validate: max 2 co-organizers
          if (currentCoOrgs.length >= 2) {
            return {
              error: { status: 'VALIDATION_ERROR', error: 'Maximum 2 co-organizers allowed' },
            };
          }

          // Validate: not the main organizer
          if (eventData?.organisation === data.orgId) {
            return {
              error: {
                status: 'VALIDATION_ERROR',
                error: 'Cannot add the main organizer as a co-organizer',
              },
            };
          }

          // Validate: not already a co-organizer
          if (currentCoOrgs.some((co) => co.orgId === data.orgId)) {
            return {
              error: {
                status: 'VALIDATION_ERROR',
                error: 'This organization is already a co-organizer',
              },
            };
          }

          const newCoOrganizer: CoOrganizer = {
            orgId: data.orgId,
            orgName: data.orgName,
            orgLogo: data.orgLogo || undefined,
            permissions: data.permissions,
            addedAt: new Date(),
            addedBy: data.addedBy,
          };

          await updateDoc(eventRef, {
            coOrganizers: arrayUnion(newCoOrganizer),
            coOrganizerIds: arrayUnion(data.orgId),
          });

          // Notify the co-organizer org's owner
          try {
            const coOrgRef = doc(DB, 'organisers', data.orgId);
            const coOrgSnap = await getDoc(coOrgRef);
            if (coOrgSnap.exists()) {
              const coOrgOwnerId = coOrgSnap.data()?.ownerid;
              if (coOrgOwnerId) {
                await addDoc(collection(DB, 'notifications'), {
                  title: 'Co-Organizer Invitation',
                  description: `Your organization "${data.orgName}" has been added as co-organizer of the event "${data.eventTitle}" by ${data.mainOrgName}.`,
                  ownerid: coOrgOwnerId,
                  avatar: null,
                  type: 'co_organizer_added',
                  createdAt: new Date(),
                  isUnRead: true,
                  eventId: data.eventId,
                  eventTitle: data.eventTitle,
                  orgName: data.mainOrgName,
                });
              }
            }
          } catch (notifError) {
            // Non-blocking: don't fail the mutation if notification fails
            console.error('Error sending co-organizer notification:', notifError);
          }

          return { data: { coOrganizer: newCoOrganizer } };
        } catch (error: any) {
          console.error('Error adding co-organizer:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'CoOrganizer', id: arg.eventId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(EventsApi.util.invalidateTags([
            { type: 'Event', id: arg.eventId },
            { type: 'Notification', id: 'LIST' },
          ]));
        } catch {}
      },
    }),

    /**
     * Update a co-organizer's permissions
     */
    updateCoOrganizerPermissions: builder.mutation<
      null,
      {
        eventId: string;
        orgId: string;
        permissions: CoOrganizerPermission;
      }
    >({
      async queryFn({ eventId, orgId, permissions }) {
        try {
          const eventRef = doc(DB, 'events', eventId);
          const eventSnap = await getDoc(eventRef);

          if (!eventSnap.exists()) {
            return { error: { status: 'NOT_FOUND', error: 'Event not found' } };
          }

          const eventData = eventSnap.data();
          const coOrganizers: CoOrganizer[] = eventData?.coOrganizers || [];

          const updatedCoOrgs = coOrganizers.map((co) =>
            co.orgId === orgId ? { ...co, permissions } : co
          );

          await updateDoc(eventRef, { coOrganizers: updatedCoOrgs });

          return { data: null };
        } catch (error: any) {
          console.error('Error updating co-organizer permissions:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'CoOrganizer', id: arg.eventId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(EventsApi.util.invalidateTags([{ type: 'Event', id: arg.eventId }]));
        } catch {}
      },
    }),

    /**
     * Remove a co-organizer from an event
     */
    removeCoOrganizer: builder.mutation<
      null,
      { eventId: string; orgId: string }
    >({
      async queryFn({ eventId, orgId }) {
        try {
          const eventRef = doc(DB, 'events', eventId);
          const eventSnap = await getDoc(eventRef);

          if (!eventSnap.exists()) {
            return { error: { status: 'NOT_FOUND', error: 'Event not found' } };
          }

          const eventData = eventSnap.data();
          const coOrganizers = (eventData?.coOrganizers || []).filter(
            (co: CoOrganizer) => co.orgId !== orgId
          );
          const coOrganizerIds = (eventData?.coOrganizerIds || []).filter(
            (id: string) => id !== orgId
          );

          await updateDoc(eventRef, { coOrganizers, coOrganizerIds });

          return { data: null };
        } catch (error: any) {
          console.error('Error removing co-organizer:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'CoOrganizer', id: arg.eventId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(EventsApi.util.invalidateTags([{ type: 'Event', id: arg.eventId }]));
        } catch {}
      },
    }),
  }),
});

export const {
  useAddCoOrganizerMutation,
  useUpdateCoOrganizerPermissionsMutation,
  useRemoveCoOrganizerMutation,
} = CoOrganizersApi;
